import * as React from 'react';

import { cn } from '../../lib/utils';

export interface RadioProps {
  /** Whether the radio is selected. Controlled. */
  checked: boolean;
  /** Callback fired when the radio's checked state changes. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Visible label rendered next to the visual ring. */
  label: string;
  /** `name` attribute forwarded to the underlying input (groups radios). */
  name: string;
  /** `value` attribute forwarded to the underlying input. */
  value: string;
  /** Disables the radio. Blocks click and keyboard activation. */
  disabled?: boolean;
  /** Optional className appended to the root wrapper. */
  className?: string;
}

/**
 * Radio atom — Default / Hover / Selected (Pressed) states per Figma `289:50630`.
 *
 * Wraps a visually-hidden native `<input type="radio">` so keyboard navigation
 * (Tab / Space) and screen-reader semantics are preserved. Visual ring is
 * 12×12 inside a 16×16 outer touch target. Selected state renders an orange
 * border + center dot; hover swaps the grey-400 border for dawn-orange.
 *
 * Disabled is treated per a11y (input.disabled) — Figma does not yet specify
 * a Disabled visual; tracked in canonicalization-map § 5.
 */
export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ checked, onChange, label, name, value, disabled = false, className }, ref) => {
    return (
      <label
        className={cn(
          'inline-flex items-center gap-2 font-outfit text-[13px] leading-[1.3] text-dawn-black',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer',
          className
        )}
      >
        <span data-radio-outer className="relative inline-flex h-4 w-4 items-center justify-center">
          <input
            ref={ref}
            type="radio"
            name={name}
            value={value}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            aria-checked={checked}
            className="peer absolute inset-0 h-full w-full cursor-[inherit] opacity-0"
          />
          <span
            data-radio-ring
            className={cn(
              'inline-flex h-3 w-3 items-center justify-center rounded-full border bg-transparent transition-colors',
              checked
                ? 'border-dawn-orange'
                : 'border-grey-400 hover:border-dawn-orange peer-hover:border-dawn-orange peer-focus-visible:border-dawn-orange'
            )}
          >
            {checked && (
              <span data-radio-dot className="block h-1.5 w-1.5 rounded-full bg-dawn-orange" />
            )}
          </span>
        </span>
        <span>{label}</span>
      </label>
    );
  }
);
Radio.displayName = 'Radio';
